import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, Search, Users, Heart, ShoppingBag, Store, Package, Gift, Sparkles } from "lucide-react";
import discordLogo from "@/assets/discord-logo.png";

const DISCORD_URL = "https://discord.gg/r8ASqAZ7N8";

const DISCOVER_IMAGES = [
  {
    visual: "business",
    title: "Business Directory",
    link: "/directory",
    surface: "bg-[#E2F8EC]",
    accent: "bg-[#BFF0D5]",
    iconTone: "text-[#1a1a1a]",
  },
  {
    visual: "market",
    title: "Market Directory",
    link: "/markets",
    surface: "bg-[#E8EFFF]",
    accent: "bg-[#CDD9FF]",
    iconTone: "text-[#1a1a1a]",
  },
  {
    visual: "events",
    title: "Upcoming Markets",
    link: "/events",
    surface: "bg-[#FFF3C9]",
    accent: "bg-[#FFE7A1]",
    iconTone: "text-[#1a1a1a]",
  }
];

const SUPPORT_ITEMS = [
  {
    icon: Users,
    label: "Become an Ambassador",
    desc: "Help spread the word and connect more people with local businesses and markets.",
    path: "/ambassador",
    surface: "bg-[#E2F8EC]",
    border: "border-[#42D78A]/35",
    iconBg: "bg-[#BFF0D5]",
  },
  {
    icon: Heart,
    label: "Become a Sponsor",
    desc: "Support the platform as it grows into a stronger resource for the Columbus local economy.",
    path: "/sponsor",
    surface: "bg-[#E8EFFF]",
    border: "border-[#BFD0FF]",
    iconBg: "bg-[#CDD9FF]",
  },
  {
    icon: ShoppingBag,
    label: "Shop Our Merch",
    desc: "Show your love for shopping local while helping fund future community tools.",
    path: "/merch",
    surface: "bg-[#FFF3C9]",
    border: "border-[#F4D16B]",
    iconBg: "bg-[#FFE7A1]",
  }
];

const JOIN_AUDIENCES = [
  {
    icon: Package,
    visual: "business",
    reverse: false,
    eyebrow: "For Businesses",
    shortTitle: "Businesses",
    title: "Put your local business on the map",
    desc: "Create a business listing so Columbus shoppers can discover your products, services, events, and story.",
    cta: "List Your Business",
    surface: "bg-[#E8FAF0]",
    border: "border-[#42D78A]/45",
  },
  {
    icon: Store,
    visual: "market",
    reverse: true,
    eyebrow: "For Markets",
    shortTitle: "Markets",
    title: "Help shoppers find your market days",
    desc: "Share your market schedule, seasonal details, pop-ups, and events with people looking to shop local.",
    cta: "Register Your Market",
    surface: "bg-[#EEF4FF]",
    border: "border-[#BFD0FF]",
  },
  {
    icon: Heart,
    visual: "shopper",
    reverse: false,
    eyebrow: "For Shoppers",
    shortTitle: "Shoppers",
    title: "Keep track of your local favorites",
    desc: "Create a shopper profile to save businesses and follow markets, then find them anytime from your profile.",
    cta: "Create Shopper Profile",
    surface: "bg-[#FFF6D8]",
    border: "border-[#F4D16B]",
  }
];

const HERO_CARDS = [
  {
    label: "Handmade Goods",
    title: "SHOP SMALL",
    caption: "Candles, art, books, vintage, and more",
    icon: Package,
    accent: "bg-[#42D78A]",
    surface: "bg-[#F3FFF8]",
    position: "left-[5vw] top-[13%] w-[300px]",
    transform: "rotate(-9deg)",
    hoverTransform: "translateY(-18px) rotate(-4deg) scale(1.06)",
  },
  {
    label: "Market Days",
    title: "FIND MARKETS",
    caption: "Seasonal, year-round, and pop-up markets",
    icon: Store,
    accent: "bg-[#BFE8D4]",
    surface: "bg-[#F8FAFC]",
    position: "left-[8vw] bottom-[11%] w-[310px]",
    transform: "rotate(7deg)",
    hoverTransform: "translateY(-18px) rotate(3deg) scale(1.06)",
  },
  {
    label: "Local Services",
    title: "BOOK LOCAL",
    caption: "Creative, wellness, home, and event services",
    icon: Users,
    accent: "bg-[#DDE7FF]",
    surface: "bg-white",
    position: "right-[6vw] top-[14%] w-[300px]",
    transform: "rotate(8deg)",
    hoverTransform: "translateY(-18px) rotate(3deg) scale(1.06)",
  },
  {
    label: "Upcoming Events",
    title: "POP-UP READY",
    caption: "Keep up with what is happening nearby",
    icon: CalendarDays,
    accent: "bg-[#FFF0C2]",
    surface: "bg-[#F7FFF9]",
    position: "right-[8vw] bottom-[10%] w-[320px]",
    transform: "rotate(-7deg)",
    hoverTransform: "translateY(-18px) rotate(-3deg) scale(1.06)",
  },
];

const HeroLocalCard = ({ card, compact = false }) => (
  <div
    className={`group ${card.surface} relative overflow-hidden rounded-[26px] border border-white/70 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.18)] transition-all duration-300 ease-out hover:-translate-y-2 hover:scale-[1.03] hover:shadow-[0_26px_55px_rgba(0,0,0,0.22)] ${compact ? "min-h-[134px]" : "min-h-[176px]"}`}
  >
    <div className={`absolute right-0 top-0 h-3 w-28 rounded-bl-2xl ${card.accent} transition-all duration-300 group-hover:w-36`} />
    <div className="relative flex h-full flex-col justify-between gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1a1a1a] text-white shadow-sm transition-transform duration-300 group-hover:rotate-[-4deg] group-hover:scale-105">
          <card.icon className="h-6 w-6" strokeWidth={1.8} />
        </div>
        <span className="rounded-full border border-[#1a1a1a]/10 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#1a1a1a]/70" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {card.label}
        </span>
      </div>
      <div>
        <p className={`${compact ? "text-2xl" : "text-3xl"} font-black leading-none text-[#1a1a1a]`} style={{ fontFamily: "'Outfit', sans-serif" }}>
          {card.title}
        </p>
        <p className="mt-2 text-xs font-medium leading-snug text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {card.caption}
        </p>
      </div>
    </div>
  </div>
);

const DiscoverVisual = ({ type, surface = "bg-white", accent = "bg-[#42D78A]", iconTone = "text-[#42D78A]" }) => {
  if (type === "market") {
    return (
      <div className={`aspect-[4/3] ${surface} p-5`}>
        <div className="mb-4 flex items-center justify-between">
          <div className="h-3 w-24 rounded-full bg-[#1a1a1a]" />
          <span className="rounded-full bg-[#1a1a1a] px-3 py-1 text-[10px] font-semibold text-white">Saturdays</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["Pop-up", "Year-round", "Markets"].map((label) => (
            <div key={label} className="rounded-xl border border-white/80 bg-white/75 px-2 py-3 text-center shadow-sm">
              <Store className={`mx-auto mb-2 h-5 w-5 ${iconTone}`} />
              <span className="text-[10px] font-bold text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>{label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-white/80 bg-white/70 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-medium text-gray-500">
            <MapPin className={`h-3.5 w-3.5 ${iconTone}`} />
            Neighborhood market
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 mb-2" />
          <div className="h-2 w-2/3 rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (type === "events") {
    return (
      <div className={`aspect-[4/3] ${surface} p-5`}>
        <div className="mb-4 flex items-center justify-between">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
            <CalendarDays className={`h-5 w-5 ${iconTone}`} />
          </div>
          <span className="rounded-full border border-white/80 bg-white/75 px-3 py-1 text-[10px] font-semibold text-gray-500">Upcoming</span>
        </div>
        <div className="space-y-3">
          {["Market Day", "Vendor Pop-up", "Community Event"].map((label, index) => (
            <div key={label} className="flex items-center gap-3 rounded-xl border border-white/80 bg-white/75 p-3 shadow-sm">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent} text-sm font-bold text-[#1a1a1a] shadow-sm`}>
                {index + 1}
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{label}</div>
                <div className="mt-1 h-2 w-3/4 rounded-full bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`aspect-[4/3] ${surface} p-5`}>
      <div className="mb-4 flex items-center gap-2 rounded-full border border-white/80 bg-white/75 px-4 py-3 text-sm text-gray-400 shadow-sm">
        <Search className="h-4 w-4" />
        Search goods and services
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "Goods", icon: Package },
          { label: "Services", icon: Users },
        ].map((item) => (
          <div key={item.label} className="rounded-xl border border-white/80 bg-white/75 p-4 shadow-sm">
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${accent} shadow-sm`}>
              <item.icon className={`h-6 w-6 ${iconTone}`} />
            </div>
            <span className="rounded-full bg-[#1a1a1a] px-3 py-1 text-[10px] font-semibold text-white">{item.label}</span>
            <div className="mt-4 h-2 w-full rounded-full bg-gray-200" />
            <div className="mt-2 h-2 w-2/3 rounded-full bg-gray-200" />
          </div>
        ))}
      </div>
    </div>
  );
};

const AudienceVisual = ({ type, surface = "bg-white", border = "border-gray-200" }) => {
  if (type === "business") {
    return (
      <div className={`rounded-2xl border ${border} ${surface} p-4 shadow-sm`}>
        <div className="flex items-center gap-2 rounded-full border border-white/80 bg-white/75 px-4 py-3 text-sm text-gray-400 mb-4 shadow-sm">
          <span className="w-3 h-3 rounded-full bg-[#42D78A]" />
          Search businesses...
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { tag: "Goods", detail: "Handmade home goods", icon: Package },
            { tag: "Services", detail: "Creative services", icon: Users },
          ].map((item) => (
            <div key={item.tag} className="rounded-xl border border-white/80 overflow-hidden bg-white/80 shadow-sm">
              <div className="relative min-h-[150px] bg-white/60 border-b border-white/80 flex items-center justify-center pt-9">
                <span className="absolute right-4 top-4 rounded-full bg-[#1a1a1a] px-4 py-1.5 text-[10px] text-white shadow-sm">{item.tag}</span>
                <item.icon className="w-11 h-11 text-[#1a1a1a]" />
              </div>
              <div className="p-3">
                <div className="h-3 w-24 rounded-full bg-gray-900 mb-2" />
                <div className="h-2 w-full rounded-full bg-gray-200 mb-2" />
                <div className="h-2 w-2/3 rounded-full bg-gray-200" />
                <div className="mt-3 flex items-center gap-1 text-[10px] text-gray-400">
                  <span className="w-2 h-2 rounded-full bg-[#42D78A]" />
                  {item.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "market") {
    return (
      <div className={`rounded-2xl border ${border} ${surface} p-4 shadow-sm`}>
        <div className="flex flex-wrap gap-2 mb-4">
          {["Saturdays", "Pop-up", "Year-round"].map((tag) => (
            <span key={tag} className={`rounded-full border px-3 py-1 text-xs font-medium ${tag === "Pop-up" ? "bg-[#1a1a1a] border-[#1a1a1a] text-white" : "border-white/80 bg-white/70 text-gray-500"}`}>{tag}</span>
          ))}
        </div>
        <div className="rounded-xl border border-white/80 overflow-hidden bg-white/80 shadow-sm">
          <div className="relative aspect-[16/9] bg-white/60 border-b border-white/80 flex items-center justify-center">
            <span className="absolute top-3 right-3 rounded-full bg-[#1a1a1a] px-3 py-1 text-[10px] text-white">Saturdays</span>
            <Store className="w-12 h-12 text-[#1a1a1a]" />
          </div>
          <div className="p-4">
            <div className="h-3 w-36 rounded-full bg-gray-900 mb-3" />
            <div className="h-2 w-full rounded-full bg-gray-200 mb-2" />
            <div className="h-2 w-3/4 rounded-full bg-gray-200 mb-4" />
            <div className="space-y-2">
              <div className="h-2 w-28 rounded-full bg-gray-200" />
              <div className="h-2 w-24 rounded-full bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border ${border} ${surface} p-4 shadow-sm`}>
      <div className="flex items-center justify-between border-b border-white/80 pb-4 mb-4">
        <div>
          <div className="h-3 w-32 rounded-full bg-gray-900 mb-2" />
          <div className="h-2 w-40 rounded-full bg-gray-200" />
        </div>
        <div className="w-10 h-10 rounded-full bg-[#42D78A]/10 flex items-center justify-center">
          <Heart className="w-5 h-5 text-[#1a1a1a]" />
        </div>
      </div>
      <div className="space-y-3">
        {["Saved Businesses", "Followed Markets"].map((label, index) => (
          <div key={label} className="rounded-xl border border-white/80 bg-white/75 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{label}</span>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{index + 2}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="h-16 rounded-lg border border-white/80 bg-white/70 flex items-center justify-center"><Heart className="w-4 h-4 text-[#1a1a1a]" /></div>
              <div className="h-16 rounded-lg border border-white/80 bg-white/70 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#1a1a1a]" /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CommunityVisual = () => (
  <div className="relative min-h-[260px] p-8">
    <div className="relative h-full flex flex-col items-center justify-center text-center">
      <div className="w-36 h-36 flex items-center justify-center">
        <img src={discordLogo} alt="Discord" className="w-full h-full object-contain" />
      </div>
      <div className="mt-5 rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-gray-500 shadow-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        Community Hub
      </div>
    </div>
  </div>
);

const HomePage = () => {
  const [hoveredHeroCard, setHoveredHeroCard] = useState(null);
  const [activeJoinCard, setActiveJoinCard] = useState(0);

  return (
    <div className="page-fade-in">
      {/* Hero Section */}
      <section
        className="relative overflow-hidden bg-[#EEF8F2] px-6 py-16 sm:py-20 lg:min-h-[78vh] lg:py-24"
        data-testid="hero-section"
      >
        {HERO_CARDS.map((card) => (
          <div
            key={card.label}
            className={`absolute z-20 hidden cursor-pointer transition-transform duration-300 ease-out xl:block ${card.position}`}
            style={{ transform: hoveredHeroCard === card.label ? card.hoverTransform : card.transform }}
            onMouseEnter={() => setHoveredHeroCard(card.label)}
            onMouseLeave={() => setHoveredHeroCard(null)}
            aria-hidden="true"
          >
            <HeroLocalCard card={card} />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex min-h-[58vh] max-w-6xl flex-col items-center justify-center text-center">
          <div className="mb-10 hidden w-full max-w-2xl grid-cols-2 gap-3 md:grid xl:hidden">
            {HERO_CARDS.map((card) => (
              <HeroLocalCard key={card.label} card={card} compact />
            ))}
          </div>
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[#1a1a1a]/10 bg-white text-[#1a1a1a] shadow-[0_16px_35px_rgba(26,26,26,0.12)]">
            <ShoppingBag className="h-7 w-7" strokeWidth={1.8} />
          </div>
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.26em] text-[#2ABF72]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Shop local. Find local.
          </p>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#1a1a1a] mb-4 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            614 Shop Local
          </h1>
          <p
            className="mx-auto max-w-2xl text-[#1a1a1a]/70 text-sm md:text-base tracking-[0.15em] uppercase mb-8"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Connecting small businesses, shoppers, and markets together
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button
                className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-10 py-6 text-base font-bold transition-all hover:-translate-y-1 shadow-lg"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="hero-join-btn"
              >
                Join 614 Shop Local
              </Button>
            </Link>
            <Link to="/directory">
              <Button
                variant="outline"
                className="bg-white hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/10 rounded-full px-10 py-6 text-base font-bold transition-all hover:-translate-y-1 shadow-lg"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="hero-directory-btn"
              >
                Visit the Directory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Discover Section */}
      <section className="py-20 md:py-28 px-6 bg-white" data-testid="discover-section">
        <div className="max-w-5xl mx-auto text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-4 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Discover Local Shops & Vendors
            <br />
            in Columbus
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14 stagger-in">
            {DISCOVER_IMAGES.map((item) => (
              <Link
                key={item.title}
                to={item.link}
                className="group block"
                data-testid={`discover-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-md transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl">
                  <DiscoverVisual type={item.visual} surface={item.surface} accent={item.accent} iconTone={item.iconTone} />
                </div>
                <h3
                  className="mt-5 text-lg md:text-xl font-bold text-[#1a1a1a] group-hover:text-[#42D78A] transition-colors"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {item.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Join Section */}
      <section className="py-16 md:py-20 px-6 bg-[#FBFDFB]" data-testid="join-audience-section">
        <div className="max-w-6xl mx-auto border-y border-gray-100 py-12">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-3 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Join 614 Shop Local</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Choose how you want to connect locally
            </h2>
            <p className="text-gray-600 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Whether you run a business, host a market, or love shopping local, there is a place for you here.
            </p>
          </div>
          <div className="flex flex-col gap-5 lg:h-[470px] lg:flex-row lg:items-stretch">
            {JOIN_AUDIENCES.map((item, index) => {
              const isActive = activeJoinCard === index;
              return (
                <div
                  key={item.eyebrow}
                  className={`${item.surface} border-2 ${item.border} group relative overflow-hidden rounded-2xl p-6 shadow-sm transition-all duration-500 ease-out hover:shadow-xl lg:p-7 ${isActive ? "lg:flex-[2.2]" : "lg:flex-[0.62] lg:cursor-pointer"}`}
                  onClick={() => setActiveJoinCard(index)}
                  onFocus={() => setActiveJoinCard(index)}
                  onMouseEnter={() => setActiveJoinCard(index)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActiveJoinCard(index);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-expanded={isActive}
                  data-testid={`join-card-${item.eyebrow.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {isActive ? (
                    <div className="grid h-full gap-7 lg:grid-cols-[0.88fr_1.12fr] lg:items-center">
                      <div>
                        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#1a1a1a] shadow-sm">
                          <item.icon className="h-7 w-7" strokeWidth={1.8} />
                        </div>
                        <p className="text-[#2ABF72] text-xs tracking-[0.18em] uppercase mb-3 font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.eyebrow}</p>
                        <h3 className="text-2xl md:text-4xl font-bold text-[#1a1a1a] mb-4 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                        <p className="text-gray-700 leading-relaxed mb-7 max-w-xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.desc}</p>
                        <Link to="/register" onClick={(event) => event.stopPropagation()}>
                          <Button
                            className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                            data-testid={`home-${item.eyebrow.toLowerCase().replace(/\s+/g, '-')}-btn`}
                          >
                            {item.cta}
                          </Button>
                        </Link>
                      </div>
                      <AudienceVisual type={item.visual} surface={item.surface} border={item.border} />
                    </div>
                  ) : (
                    <div className="flex h-full flex-col justify-between gap-5 lg:justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#1a1a1a] shadow-sm lg:hidden">
                        <item.icon className="h-7 w-7" strokeWidth={1.8} />
                      </div>
                      <div className="lg:hidden">
                        <p className="text-[#2ABF72] text-xs tracking-[0.18em] uppercase mb-3 font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.eyebrow}</p>
                        <h3 className="text-2xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                        <p className="text-gray-700 leading-relaxed mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.desc}</p>
                        <Link to="/register" onClick={(event) => event.stopPropagation()}>
                          <Button
                            className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                            data-testid={`home-${item.eyebrow.toLowerCase().replace(/\s+/g, '-')}-btn`}
                          >
                            {item.cta}
                          </Button>
                        </Link>
                      </div>
                      <div className="hidden flex-1 items-center justify-center lg:flex">
                        <div className="max-w-[150px] text-center">
                          <div className="mx-auto mb-7 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-[#1a1a1a] shadow-sm">
                            <item.icon className="h-8 w-8" strokeWidth={1.8} />
                          </div>
                          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#2ABF72]" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.eyebrow}</p>
                          <h3 className="text-2xl font-bold leading-tight text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.shortTitle}</h3>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-16 md:py-20 px-6 bg-[#F8FAFC]" data-testid="discord-community-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <CommunityVisual />
            <div>
              <div className="w-12 h-12 rounded-full bg-[#42D78A]/10 flex items-center justify-center mb-5">
                <Users className="w-6 h-6 text-[#42D78A]" />
              </div>
              <p className="text-[#42D78A] text-xs tracking-[0.18em] uppercase mb-3 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Online Community</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Join the 614 Shop Local Hub on Discord
              </h2>
              <p className="text-[#1a1a1a] font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Our hub is managed through The Shop Local Collective Discord.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6 max-w-2xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Connect with shoppers, small businesses, markets, and local supporters in one online community built around strengthening the local economy.
              </p>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="home-discord-community-btn"
                >
                  Join the Discord
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 md:py-28 bg-[#FBFDFB] px-6" data-testid="support-section">
        <div className="max-w-6xl mx-auto text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Want to Support Us?
          </h2>
          <p
            className="text-gray-600 max-w-2xl mx-auto mb-14 text-base leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            614 Shop Local is a community driven platform, made to help support the local businesses & markets within the Columbus and surrounding areas. We would not be able to operate without the support of community members like you. Community members can support us in some of the following ways:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12 stagger-in text-left">
            {SUPPORT_ITEMS.map((item) => (
              <Link key={item.label} to={item.path} className={`group rounded-2xl border-2 ${item.border} ${item.surface} p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg`}>
                <div className={`mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border border-white/70 ${item.iconBg} text-[#1a1a1a] transition-colors group-hover:scale-105`}>
                  <item.icon className="h-9 w-9" strokeWidth={1.8} />
                </div>
                <h3
                  className="text-xl font-bold text-[#1a1a1a] mb-3"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {item.label}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {item.desc}
                </p>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-20 md:py-28 px-6 bg-slate-50" data-testid="roadmap-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-3 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Our journey</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Road Map</h2>
            <p className="text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>How we&apos;re building a thriving local community, step by step</p>
          </div>

          {(() => {
            const phases = [
              {
                icon: Store,
                label: "Phase 1",
                title: "Online Directory",
                desc: "A searchable directory of local businesses and markets across Columbus and surrounding areas.",
                surface: "bg-[#E2F8EC]",
                border: "border-[#42D78A]/40",
                iconBg: "bg-[#BFF0D5]",
                labelColor: "text-[#1D9F5D]",
                dot: "bg-[#42D78A] ring-[#42D78A]/20",
              },
              {
                icon: ShoppingBag,
                label: "Phase 2",
                title: "Online Marketplace",
                desc: "A unified marketplace connecting local shoppers directly to small businesses.",
                surface: "bg-[#E8EFFF]",
                border: "border-[#BFD0FF]",
                iconBg: "bg-[#CDD9FF]",
                labelColor: "text-[#5572D8]",
                dot: "bg-[#7E95F2] ring-[#7E95F2]/20",
              },
              {
                icon: Sparkles,
                label: "Phase 3",
                title: "Future Features",
                desc: "Continually expanding the directory and app with new ways to discover and support local businesses.",
                surface: "bg-[#FFF3C9]",
                border: "border-[#F4D16B]",
                iconBg: "bg-[#FFE7A1]",
                labelColor: "text-[#B08316]",
                dot: "bg-[#F4C84D] ring-[#F4C84D]/25",
              },
            ];
            const sub = [
              {
                icon: Package,
                title: "Shop Local Discovery Box",
                desc: "Monthly, bi-monthly, and quarterly boxes featuring products from local businesses.",
                surface: "bg-[#E8FAF0]",
                border: "border-[#42D78A]/35",
                iconBg: "bg-[#BFF0D5]",
                iconColor: "text-[#1a1a1a]",
              },
              {
                icon: Gift,
                title: "Corporate Gifting",
                desc: "Custom gift packages for businesses sourced from local Columbus vendors.",
                surface: "bg-[#E8EFFF]",
                border: "border-[#BFD0FF]",
                iconBg: "bg-[#CDD9FF]",
                iconColor: "text-[#1a1a1a]",
              },
            ];

            return (
              <>
                {/* Desktop: 3 phase cards in a row, with 2 sub-feature cards under the gaps */}
                <div className="hidden md:block">
                  <div className="grid grid-cols-3 gap-6 mb-8">
                    {phases.map((p, i) => (
                      <RoadmapCard key={i} {...p} />
                    ))}
                  </div>

                  {/* Timeline with dots + connector lines */}
                  <div className="relative h-10 mb-2">
                    {/* Horizontal line */}
                    <div className="absolute left-[8%] right-[8%] top-1/2 h-px bg-gray-200 -translate-y-1/2" />
                    {/* Phase dots — aligned to centers of 3-column grid: 1/6, 3/6, 5/6 */}
                    {[1, 3, 5].map((n, i) => (
                      <div
                        key={`phase-${n}`}
                        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${(n / 6) * 100}%` }}
                      >
                        {/* line going UP to the phase card (52px to bridge mb-8 + half timeline) */}
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-1/2 w-px bg-gray-200" style={{ height: "52px" }} />
                        {/* dot */}
                        <div className={`relative w-3 h-3 rounded-full ring-4 ${phases[i].dot}`} />
                      </div>
                    ))}
                    {/* Sub-feature dots — centers of 2 sub cards inside px-[16%] container */}
                    {[0.25, 0.75].map((p, i) => (
                      <div
                        key={`sub-${i}`}
                        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${16 + 68 * p}%` }}
                      >
                        {/* dot */}
                        <div className="relative w-3 h-3 rounded-full bg-white border-2 border-gray-300" />
                        {/* line going DOWN to the sub card (~28px to bridge mb-2 + half timeline) */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 w-px bg-gray-200" style={{ height: "28px" }} />
                      </div>
                    ))}
                  </div>

                  {/* Sub-feature row aligned to gaps */}
                  <div className="grid grid-cols-6 gap-6 px-[16%]">
                    {sub.map((s, i) => (
                      <div key={i} className="col-span-3">
                        <RoadmapCard {...s} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile: vertical timeline */}
                <div className="md:hidden relative pl-8">
                  <div className="absolute left-[7px] top-3 bottom-3 w-px bg-gray-200" />
                  <div className="space-y-4">
                    {[phases[0], sub[0], phases[1], sub[1], phases[2]].map((item, i) => {
                      return (
                        <div key={i} className="relative">
                          <div
                            className={`absolute -left-[26px] top-7 w-3 h-3 rounded-full ${item.dot ? `ring-4 ${item.dot}` : "bg-white border-2 border-gray-300"}`}
                          />
                          <RoadmapCard {...item} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>
    </div>
  );
};

const RoadmapCard = ({
  icon: Icon,
  label,
  title,
  desc,
  surface = "bg-white",
  border = "border-gray-100",
  iconBg = "bg-white",
  iconColor = "text-[#1a1a1a]",
  labelColor = "text-[#42D78A]",
}) => (
  <div className={`relative rounded-2xl border-2 ${border} ${surface} p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md`} data-testid={`roadmap-card-${(title || label).toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${iconBg}`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    {label && (
      <p className={`text-xs font-bold tracking-[0.15em] uppercase ${labelColor} mb-1`} style={{ fontFamily: "'Montserrat', sans-serif" }}>{label}</p>
    )}
    <h4 className="font-bold text-lg text-[#1a1a1a] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h4>
    <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>{desc}</p>
  </div>
);

export default HomePage;
